// Export der Dienstleisterliste nach Feldgruppen (Handoff Dienstleister §7) über lib/export (exportCsv / exportXlsx).

import type { ExportColumn, ExportConfig } from '../export/types';
import { deDatum } from '../immobilien/objektDetail';
import { objektName } from '../immobilien/objektKopf';
import { aktuelleNachweise, type NachweisArt } from './nachweise';
import { istVertrag, zuordnungLaeuft, type DlObjektRoh, type DlZeile, type NachweisZeileRoh, type ObjektRoh } from './liste';
import { statusText } from './status';

export type ExportGruppe = 'stammdaten' | 'kategorien' | 'objekte' | 'nachweise' | 'rechnungen' | 'bank';

export const EXPORT_GRUPPEN: readonly { key: ExportGruppe; label: string; standard: boolean }[] = [
  { key: 'stammdaten', label: 'Stammdaten & Kontakt', standard: true },
  { key: 'kategorien', label: 'Kategorien & Flags (Notfall, bevorzugt, Beauftragung)', standard: true },
  { key: 'objekte', label: 'Objekt-Zuordnungen & Verträge', standard: true },
  { key: 'nachweise', label: 'Nachweise mit Ablaufdatum', standard: false },
  { key: 'rechnungen', label: 'Rechnungen & Volumen', standard: false },
  { key: 'bank', label: 'Bank & Steuer', standard: false },
];
export const standardGruppen = (): Set<ExportGruppe> => new Set(EXPORT_GRUPPEN.filter(g => g.standard).map(g => g.key));

const ART_LABEL: Record<string, string> = { firma: 'Firma', einzel: 'Einzelunternehmen', behoerde: 'Behörde / Versorger' };

const SPALTEN: Record<ExportGruppe, readonly ExportColumn[]> = {
  stammdaten: [
    { key: 'name', label: 'Name' }, { key: 'art', label: 'Art' }, { key: 'ansprechpartner', label: 'Ansprechpartner' }, { key: 'email', label: 'E-Mail' },
    { key: 'telefon', label: 'Telefon' }, { key: 'notdienst_telefon', label: 'Notdienst-Telefon' }, { key: 'website', label: 'Website' },
    { key: 'strasse', label: 'Straße' }, { key: 'hausnummer', label: 'Nr.' }, { key: 'plz', label: 'PLZ' }, { key: 'stadt', label: 'Stadt' }, { key: 'status', label: 'Status' },
  ],
  kategorien: [
    { key: 'kategorien', label: 'Kategorien' }, { key: 'handwerk', label: 'Handwerk' }, { key: 'notfall', label: 'Notfall 24/7' },
    { key: 'bevorzugt', label: 'Bevorzugt' }, { key: 'beauftragung', label: 'Beauftragung' }, { key: 'freigabegrenze', label: 'Freigabegrenze' },
    { key: 'stundensatz', label: 'Stundensatz' },
  ],
  objekte: [{ key: 'anzahl_objekte', label: 'Anzahl Objekte' }, { key: 'objekte', label: 'Objekte' }, { key: 'vertraege', label: 'Verträge' }],
  nachweise: [
    { key: 'nw_freistellung_48b', label: 'Freistellung § 48b gültig bis' }, { key: 'nw_haftpflicht', label: 'Betriebshaftpflicht gültig bis' },
    { key: 'nw_gewerbe', label: 'Gewerbe / Handwerksrolle' }, { key: 'nw_mindestlohn', label: 'Mindestlohn-/Nachunternehmererklärung' },
  ],
  rechnungen: [
    { key: 'volumen', label: 'Volumen (Jahr)' }, { key: 'offen', label: 'Offene Rechnungen' }, { key: 'auftraege', label: 'Offene Aufträge' }, { key: 'bewertung', label: 'Bewertung' },
  ],
  bank: [
    { key: 'iban', label: 'IBAN' }, { key: 'kontoinhaber', label: 'Kontoinhaber' }, { key: 'bic', label: 'BIC' }, { key: 'ust_id', label: 'USt-IdNr.' },
    { key: 'steuer_nr', label: 'Steuer-Nr.' }, { key: 'kleinunternehmer', label: 'Kleinunternehmer § 19' }, { key: 'reverse_charge_13b', label: '§ 13b Reverse Charge' },
  ],
};

export function exportSpalten(gruppen: ReadonlySet<ExportGruppe>): ExportColumn[] {
  return EXPORT_GRUPPEN.filter(g => gruppen.has(g.key)).flatMap(g => SPALTEN[g.key]);
}

export interface DlExportDaten {
  roh: ReadonlyMap<string, Record<string, unknown>>;
  dlObjekte: readonly DlObjektRoh[];
  objekte: readonly ObjektRoh[];
  nachweise: readonly NachweisZeileRoh[];
}

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();
const jaNein = (v: unknown) => (v ? 'ja' : 'nein');
const betrag = (n: number) => (n ? n.toFixed(2).replace('.', ',') : '');
const zahlText = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(v).replace('.', ','));

export function exportZeile(z: DlZeile, d: DlExportDaten, heute: string): Record<string, string> {
  const r = d.roh.get(z.id) ?? {};
  const obj = new Map(d.objekte.map(o => [o.id, o]));
  const vertraege = d.dlObjekte.filter(o => o.dienstleister_id === z.id && zuordnungLaeuft(o, heute) && istVertrag(o.vertragsart)).map(o => {
    const name = obj.get(o.objekt_id);
    return [text(o.vertragsart), name ? objektName(name) : '', o.bis ? `bis ${deDatum(o.bis.slice(0, 10))}` : ''].filter(Boolean).join(' ');
  });
  const nw = new Map(aktuelleNachweise(d.nachweise.filter(n => n.dienstleister_id === z.id)).map(n => [n.art as NachweisArt, n]));
  const nwText = (art: NachweisArt) => {
    const n = nw.get(art);
    return !n ? '' : n.gueltig_bis ? deDatum(n.gueltig_bis.slice(0, 10)) : 'unbefristet';
  };
  return {
    name: z.name, art: ART_LABEL[text(r.art)] ?? '', ansprechpartner: z.ansprechpartner, email: z.email, telefon: z.telefon,
    notdienst_telefon: text(r.notdienst_telefon), website: text(r.website), strasse: text(r.strasse), hausnummer: text(r.hausnummer), plz: text(r.plz), stadt: z.stadt,
    status: statusText(z.status, z.vertragEndet),
    kategorien: z.kategorien.join(', '), handwerk: jaNein(z.handwerk), notfall: jaNein(z.notfall), bevorzugt: z.bevorzugt ? (r.vertretung ? 'Vertretung' : 'ja') : 'nein',
    beauftragung: z.angebot ? 'Erst Angebot' : 'Direkt beauftragen', freigabegrenze: zahlText(r.freigabegrenze), stundensatz: zahlText(r.stundensatz),
    anzahl_objekte: String(z.objektIds.length), objekte: z.objektNamen.join(', '), vertraege: vertraege.join('; '),
    nw_freistellung_48b: nwText('freistellung_48b'), nw_haftpflicht: nwText('haftpflicht'), nw_gewerbe: nwText('gewerbe'), nw_mindestlohn: nwText('mindestlohn'),
    volumen: betrag(z.volumen), offen: betrag(z.offen), auftraege: String(z.offeneAuftraege), bewertung: z.bewertung === null ? '' : String(z.bewertung).replace('.', ','),
    iban: text(r.iban), kontoinhaber: text(r.kontoinhaber), bic: text(r.bic), ust_id: text(r.ust_id), steuer_nr: text(r.steuer_nr),
    kleinunternehmer: jaNein(r.kleinunternehmer), reverse_charge_13b: jaNein(r.reverse_charge_13b),
  };
}

export function exportKonfig(zeilen: readonly DlZeile[], d: DlExportDaten, gruppen: ReadonlySet<ExportGruppe>, heute: string): ExportConfig {
  const daten = zeilen.map(z => exportZeile(z, d, heute));
  return {
    title: 'Dienstleister',
    fileName: `dienstleister_${heute}`,
    columns: exportSpalten(gruppen),
    data: daten,
    stats: { items: [
      { label: 'Dienstleister', value: daten.length },
      { label: 'Aktiv', value: zeilen.filter(z => z.aktiv).length },
      { label: 'Inaktiv', value: zeilen.filter(z => !z.aktiv).length },
    ] },
  };
}
